"use client";

import { memo, useCallback, useMemo } from "react";
import HotelCard from "@/components/card/HotelCard";
import MediaCardSkeleton from "@/components/skeletons/card/MediaCardSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { Typography } from "@/components/Typography";
import { mapSearchHotelCard } from "@/components/search-results/results/search.result.mapper";
import { SERVICE_EMPTY_STATE_TUPLES } from "@/components/search-results/search.result.types";
import { useHandleSearchHotelResults } from "@/hooks/hotel/useHotelSearchResults";
import { useObserver } from "@/hooks/useObserver";
import {
  type BookForMeFormData,
  buildBookForMeSearchParams,
} from "../book-for-me.type";
import { BookForMeHelpBanner } from "./BookForMeComponents";
import BookForMeRequestSummary from "./BookForMeRequestSummary";

interface BookForMeResultsViewProps {
  data: BookForMeFormData;
}

export const BookForMeResultsView = memo(function BookForMeResultsView({
  data,
}: BookForMeResultsViewProps) {
  const searchParams = useMemo(() => buildBookForMeSearchParams(data), [data]);

  const {
    results,
    isLoadingResults,
    isLoadingMoreResults,
    hasMoreResults,
    loadMoreResults,
    error,
  } = useHandleSearchHotelResults(searchParams);

  const hotels = useMemo(
    () => results.map((result) => mapSearchHotelCard(result)),
    [results],
  );

  const handleIntersect = useCallback(() => {
    if (!hasMoreResults || isLoadingMoreResults) return;
    loadMoreResults();
  }, [hasMoreResults, isLoadingMoreResults, loadMoreResults]);

  const loadMoreRef = useObserver({
    onIntersect: handleIntersect,
    rootMargin: "300px",
    enabled: Boolean(
      hasMoreResults && !isLoadingResults && !isLoadingMoreResults,
    ),
  });

  const buildHotelHref = useCallback(
    (hotel: { id: number; title: string }) =>
      `/hotels/${hotel.id}?${new URLSearchParams({
        adults: `${searchParams.adults}`,
        children: `${searchParams.children}`,
        rooms: `${searchParams.rooms}`,
        search: hotel.title,
        start: searchParams.start,
        end: searchParams.end,
      })}`,
    [searchParams],
  );

  const showInitialSkeleton = isLoadingResults && hotels.length === 0;
  const showEmptyState = !isLoadingResults && hotels.length === 0;
  const [emptyTitle, emptyDescription] = SERVICE_EMPTY_STATE_TUPLES.hotel;

  return (
    <main className="space-y-4">
      <section className="mt-6">
        <Typography
          variant="subtitle1"
          mdVariant="h4"
          className="text-heading font-semibold"
        >
          We found hotels for you
        </Typography>
        <Typography variant="body2" className="text-body mt-1">
          Swipe to explore the options we found for your request
        </Typography>
      </section>

      {/* Page Header & Content */}
      <div className="flex flex-col-reverse lg:flex-row justify-between gap-4">
        <section className="w-full space-y-4 md:sticky md:top-40 self-start">
          <BookForMeRequestSummary data={data} />
          <BookForMeHelpBanner />
        </section>

        <section className="w-full">
          {showEmptyState ? (
            <EmptyState
              variant="not-available"
              title={error ? "Something went wrong" : emptyTitle}
              description={error ? error.message : emptyDescription}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {showInitialSkeleton
                ? Array.from({ length: 4 }).map((_, index) => (
                    <MediaCardSkeleton key={`hotel-skeleton-${index}`} />
                  ))
                : hotels.map((hotel) => (
                    <HotelCard
                      key={hotel.id}
                      hotel={hotel}
                      href={buildHotelHref(hotel)}
                      className="w-full max-w-none"
                    />
                  ))}

              {!showInitialSkeleton && hasMoreResults ? (
                <div
                  ref={loadMoreRef}
                  className="col-span-full h-6 w-full"
                  aria-hidden="true"
                />
              ) : null}

              {isLoadingMoreResults
                ? Array.from({ length: 2 }).map((_, index) => (
                    <MediaCardSkeleton key={`hotel-skeleton-more-${index}`} />
                  ))
                : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
});

export default BookForMeResultsView;
